import { withIdentity } from "flavours/glitch/identity_context";
import PropTypes from "prop-types";
import { Component, Fragment } from "react";

import { defineMessages, injectIntl } from "react-intl";

import { connect } from "react-redux";

import { openComposePopout } from "flavours/glitch/actions/compose";
import NavigationPortal from "flavours/glitch/components/navigation_portal";
import { timelinePreview, trendsEnabled } from "flavours/glitch/initial_state";
import { transientSingleColumn } from "flavours/glitch/is_mobile";
import { parseNavigationHidden, parseNavigationOrder } from "flavours/glitch/utils/navigation_order";

import ColumnLink from "./column_link";
import DisabledAccountBanner from "./disabled_account_banner";
import FollowRequestsColumnLink from "./follow_requests_column_link";
import ListPanel from "./list_panel";
import NotificationsCounterIcon from "./notifications_counter_icon";
import SignInBanner from "./sign_in_banner";

const messages = defineMessages({
  compose: { id: "compose_form.publish_form", defaultMessage: "New post" },
  home: { id: "tabs_bar.home", defaultMessage: "Home" },
  notifications: { id: "tabs_bar.notifications", defaultMessage: "Notifications" },
  explore: { id: "explore.title", defaultMessage: "Explore" },
  firehose: { id: "column.firehose", defaultMessage: "Public timeline" },
  direct: { id: "navigation_bar.direct", defaultMessage: "Private mentions" },
  favourites: { id: "navigation_bar.favourites", defaultMessage: "Favorites" },
  bookmarks: { id: "navigation_bar.bookmarks", defaultMessage: "Bookmarks" },
  drafts: { id: "navigation_bar.drafts", defaultMessage: "Drafts" },
  scheduled_statuses: { id: "navigation_bar.scheduled_statuses", defaultMessage: "Scheduled posts" },
  lists: { id: "navigation_bar.lists", defaultMessage: "Lists" },
  preferences: { id: "navigation_bar.preferences", defaultMessage: "Preferences" },
  followsAndFollowers: { id: "navigation_bar.follows_and_followers", defaultMessage: "Follows and followers" },
  about: { id: "navigation_bar.about", defaultMessage: "About" },
  search: { id: "navigation_bar.search", defaultMessage: "Search" },
  advancedInterface: { id: "navigation_bar.advanced_interface", defaultMessage: "Open in advanced web interface" },
  openedInClassicInterface: { id: "navigation_bar.opened_in_classic_interface", defaultMessage: "Posts, accounts, and other specific pages are opened by default in the classic web interface." },
  account_settings: { id: "navigation_bar.account_settings", defaultMessage: "Account settings" },
  app_settings: { id: "navigation_bar.app_settings", defaultMessage: "App settings" },
  logout: { id: "navigation_bar.logout", defaultMessage: "Log out" },
});

class NavigationPanel extends Component {

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  static propTypes = {
    intl: PropTypes.object.isRequired,
    onLogout: PropTypes.func,
    navigationOrder: PropTypes.string,
    navigationHidden: PropTypes.string,
    popoutEnabled: PropTypes.bool,
    onOpenComposer: PropTypes.func,
  };

  componentDidMount () {
    this.lastTops = new WeakMap();
    this.touchY = null;
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    document.addEventListener("scroll", this.handleScroll, { capture: true, passive: true });
    document.addEventListener("touchstart", this.handleTouchStart, { passive: true });
    document.addEventListener("touchmove", this.handleTouchMove, { passive: true });
  }

  componentWillUnmount () {
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("scroll", this.handleScroll, { capture: true });
    document.removeEventListener("touchstart", this.handleTouchStart);
    document.removeEventListener("touchmove", this.handleTouchMove);
    document.documentElement.classList.remove("bottom-nav-hidden");
  }

  // Untere Leiste (schmale Bildschirme): beim Runterscrollen ausblenden, beim Hochscrollen einblenden
  setHidden = (hidden) => {
    document.documentElement.classList.toggle("bottom-nav-hidden", hidden);
  };

  getScroller = (target) => {
    if (!target || target === document || target === window || target === document.documentElement || target === document.body) {
      return document.scrollingElement || document.documentElement;
    }

    return target;
  };

  handleScroll = (e) => {
    const target = this.getScroller(e.target);

    if (!target.closest || target.closest(".navigation-panel")) {
      return;
    }

    const top = target === (document.scrollingElement || document.documentElement) ? (window.scrollY || target.scrollTop) : target.scrollTop;

    if (!this.lastTops.has(target)) {
      this.lastTops.set(target, top);
      return;
    }

    const delta = top - this.lastTops.get(target);

    if (Math.abs(delta) < 8) {
      return;
    }

    this.lastTops.set(target, top);
    this.setHidden(delta > 0 && top > 60);
  };

  // Fallback fuer Browser, die beim Scrollen keine verwertbaren scroll-Events liefern (z. B. Firefox Android)
  handleTouchStart = (e) => {
    this.touchY = e.touches.length === 1 ? e.touches[0].clientY : null;
  };

  handleTouchMove = (e) => {
    if (this.touchY === null || e.touches.length !== 1 || (e.target.closest && e.target.closest(".navigation-panel"))) {
      return;
    }

    const y = e.touches[0].clientY;
    const delta = this.touchY - y;

    if (Math.abs(delta) < 12) {
      return;
    }

    this.touchY = y;

    // Finger nach oben = Inhalt scrollt nach unten
    if (delta > 0) {
      const scroller = e.target.closest ? e.target.closest(".scrollable") : null;
      const top = scroller ? scroller.scrollTop : (window.scrollY || document.documentElement.scrollTop);

      if (top > 60) {
        this.setHidden(true);
      }
    } else {
      this.setHidden(false);
    }
  };

  // The single-column layout writes in the pop-out composer instead of the compose page.
  handleComposeClick = (e) => {
    if (this.props.popoutEnabled) {
      e.preventDefault();
      this.props.onOpenComposer();
    }
  };

  isFirehoseActive = (match, location) => {
    return match || location.pathname.startsWith("/public");
  };

  render() {
    const { intl, onLogout, navigationOrder, navigationHidden } = this.props;
    const { signedIn, disabledAccountId } = this.props.identity;

    // Reihenfolge der Eintraege ist ueber die App-Einstellungen aenderbar
    const items = {
      compose: signedIn && <ColumnLink key='compose' data-nav-item='compose' transparent to='/publish' icon='pencil-simple-line' text={intl.formatMessage(messages.compose)} onClickCapture={this.handleComposeClick} />,
      home: signedIn && <ColumnLink key='home' transparent to='/home' icon='house-line' text={intl.formatMessage(messages.home)} />,
      notifications: signedIn && (
        <Fragment key='notifications'>
          <ColumnLink transparent to='/notifications' icon={<NotificationsCounterIcon className='column-link__icon' />} text={intl.formatMessage(messages.notifications)} />
          <FollowRequestsColumnLink />
        </Fragment>
      ),
      explore: trendsEnabled ? (
        <ColumnLink key='explore' transparent to='/explore' icon='hashtag' text={intl.formatMessage(messages.explore)} />
      ) : signedIn && (
        <ColumnLink key='explore' transparent to='/search' icon='magnifying-glass' text={intl.formatMessage(messages.search)} />
      ),
      public: (signedIn || timelinePreview) && (
        <ColumnLink key='public' transparent to='/public' isActive={this.isFirehoseActive} icon='fediverse' text={intl.formatMessage(messages.firehose)} />
      ),
      direct: signedIn && <ColumnLink key='direct' transparent to='/conversations' icon='at' text={intl.formatMessage(messages.direct)} />,
      bookmarks: signedIn && <ColumnLink key='bookmarks' transparent to='/bookmarks' icon='bookmarks' text={intl.formatMessage(messages.bookmarks)} />,
      drafts: signedIn && <ColumnLink key='drafts' data-nav-item='drafts' transparent to='/drafts' icon='floppy-disk' text={intl.formatMessage(messages.drafts)} />,
      scheduled_statuses: signedIn && <ColumnLink key='scheduled_statuses' data-nav-item='scheduled_statuses' transparent to='/scheduled_statuses' icon='clock' text={intl.formatMessage(messages.scheduled_statuses)} />,
      favourites: signedIn && <ColumnLink key='favourites' transparent to='/favourites' icon='star' text={intl.formatMessage(messages.favourites)} />,
      lists: signedIn && <ColumnLink key='lists' transparent to='/lists' icon='list' text={intl.formatMessage(messages.lists)} />,
      account_settings: signedIn && <ColumnLink key='account_settings' data-nav-item='account_settings' transparent to='/account-settings' icon='user-circle' text={intl.formatMessage(messages.account_settings)} />,
      app_settings: signedIn && <ColumnLink key='app_settings' data-nav-item='app_settings' transparent to='/app-settings' icon='gear' text={intl.formatMessage(messages.app_settings)} />,
    };

    return (
      <div className='navigation-panel'>
        {transientSingleColumn && (
          <div className='navigation-panel__logo'>
            <div className='switch-to-advanced'>
              {intl.formatMessage(messages.openedInClassicInterface)}
              {" "}
              <a href={`/deck${location.pathname}`} className='switch-to-advanced__toggle'>
                {intl.formatMessage(messages.advancedInterface)}
              </a>
            </div>
            <hr />
          </div>
        )}

        {parseNavigationOrder(navigationOrder).filter(id => !parseNavigationHidden(navigationHidden).includes(id)).map(id => items[id])}

        {!signedIn && (
          <div className='navigation-panel__sign-in-banner'>
            <hr />
            { disabledAccountId ? <DisabledAccountBanner /> : <SignInBanner /> }
          </div>
        )}

        {signedIn && (
          <>
            <ListPanel />

            <hr />

            <ColumnLink data-nav-item='logout' transparent onClick={onLogout} icon='sign-out' text={intl.formatMessage(messages.logout)} />
          </>
        )}

        <div className='navigation-panel__legal'>
          <hr />
          <ColumnLink transparent to='/about' icon='dots-three-outline' text={intl.formatMessage(messages.about)} />
        </div>


        <NavigationPortal />
      </div>
    );
  }

}

const mapStateToProps = state => ({
  navigationOrder: state.getIn(["local_settings", "navigation_order"]),
  navigationHidden: state.getIn(["local_settings", "navigation_hidden"]),
  popoutEnabled: state.getIn(["compose", "popout_enabled"]),
});

const mapDispatchToProps = dispatch => ({
  onOpenComposer () {
    dispatch(openComposePopout());
  },
});

export default withIdentity(injectIntl(connect(mapStateToProps, mapDispatchToProps)(NavigationPanel)));
